/* eslint-disable react/display-name */
import {
  ContactType,
  DoctorListDto,
  SubtenantDto,
  NumberSource,
  SexType,
} from '@lmt/shared-types';
import {
  CustomDictionaryName,
  useActiveDictionaryValuesByTitle,
  useDictionaryByTitle,
} from '@lmt/unicorn-shared/app/api/custom-hooks';
import {
  Field,
  FieldLinkProps,
} from '@lmt/unicorn-shared/app/components/common/Field';
import { FieldBlock } from '@lmt/unicorn-shared/app/components/common/FieldBlock';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { DatePicker } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/date-time/DatePicker';
import { ComboBoxInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/ComboBoxInput';
import {
  DropDownInput,
  HookFormDropDownInput,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { RadioButtonGroup } from '@lmt/unicorn-shared/app/components/common/ui-kit/RadioButtonGroup';
import { getDoctorFullName } from '@lmt/unicorn-shared/app/components/doctors/doctor-form/common/common';
import { minBirthDate } from '@lmt/unicorn-shared/app/components/patients/common';
import { ExternalPatientNumberBadge } from '@lmt/unicorn-shared/app/components/patients/ExternalPatientNumberBadge';
import { CustomPatientFields } from '@lmt/unicorn-shared/app/components/patients/patient-form/components/CustomPatientFields';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { validateAndDirty } from '@lmt/unicorn-shared/app/helpers/form-helper';
import {
  NS_DataBlockFields,
  useScopedTranslation,
} from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import { localizeSexType } from '@lmt/unicorn-shared/app/helpers/localization/translations';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useDebounce } from 'use-debounce';
import { levelOfCareKey, sexTypes, titleKey } from '../common/common';
import { PatientData } from '../common/types';
import { PriorityBadge } from './PriorityBadge';

import styles from './ExpandedPatientForm.module.scss';
import { TagsEditor } from '../../../common/ui-kit/tags/TagsEditor';
import { useTranslation } from 'react-i18next';
import { useGlobalForms } from '../../../global-forms/useGlobalForms';

interface PatientFormProps {
  form: UseFormReturn<PatientData>;
  isSubtenantEnabled: boolean;
  isDeceasedEnabled: boolean;
  doctors: DoctorListDto[];
  addNewDoctor: () => void;
  subtenants: SubtenantDto[];
  onIsValidChange?: (isValid: boolean) => void;
  onDirtyChange?: (dirty: boolean) => void;
  // this method shall be called before saving inputValues/sharedValues
  // it saves the ongoing edits into inputValues/sharedValues (especially needed for Tables)
  beforeSaveRef: React.Ref<() => Promise<void>>;
  onDoctorsSearch: (value: string | null) => void;
  showCustomFieldsErrors: boolean;
}

const nameof = nameofFactory<PatientData>();

export const ExpandedPatientForm: FC<PatientFormProps> = (props) => {
  const {
    form,
    isSubtenantEnabled,
    isDeceasedEnabled,
    doctors,
    subtenants,
    addNewDoctor,
    onDirtyChange,
  } = props;

  const [insuranceCompaniesSearchQuery, setInsuranceCompaniesSearchQuery] =
    useState<string | null>(null);

  const [insuranceCompaniesSearchQueryDebounced] = useDebounce(
    insuranceCompaniesSearchQuery,
    100,
  );

  const { formState, register, setValue, getValues, control, watch } = form;
  const priorityContactType = watch(nameof('priorityContactType'));
  const i18nGlobal = useTranslation();
  const i18next = useScopedTranslation('patients.form.fields');
  const i18nextDict = useTranslation(NS_DataBlockFields);
  const claims = useClaims();
  const customValues = watch('values') ?? {};
  const comments = watch('comments');
  const insuranceCompanyId = watch('healthInsuranceCompanyId');
  const globalForms = useGlobalForms();

  const { data: personTitles } = useActiveDictionaryValuesByTitle(
    CustomDictionaryName.PersonTitles,
    customValues[titleKey],
  );

  const { data: levelsOfCare } = useActiveDictionaryValuesByTitle(
    CustomDictionaryName.LevelOfCare,
    customValues[levelOfCareKey],
  );

  const insuranceCompanyDictionary = useDictionaryByTitle(
    CustomDictionaryName.InsuranceCompanies,
  );
  const { data: insuranceCompanies = [], isLoading } =
    useActiveDictionaryValuesByTitle(
      CustomDictionaryName.InsuranceCompanies,
      insuranceCompanyId,
      insuranceCompaniesSearchQueryDebounced ?? undefined,
    );

  function changePriorityContactType(contactType: ContactType) {
    setValue(nameof('priorityContactType'), contactType, validateAndDirty);
  }

  useEffect(() => {
    if (!form.getValues('isDeceased')) {
      setValue(nameof('dateOfDeath'), null, validateAndDirty);
    }
  }, [form.watch('isDeceased')]);

  const isSelfPayer = useMemo(
    () => form.getValues('isSelfPayer'),
    [form.watch('isSelfPayer')],
  );

  useEffect(() => {
    if (isSelfPayer) {
      setValue(nameof('healthInsuranceCompanyId'), null);
      setValue(nameof('insuranceNumber'), '');
      setValue(nameof('isPrivateInsurance'), false);
    }
  }, [isSelfPayer, insuranceCompanyId]);

  function dateOfDeathOnChange(newDate: Date | null) {
    newDate && setValue(nameof('isDeceased'), true, validateAndDirty);
  }

  const addNewInsuranceCompany = useCallback(async () => {
    if (!insuranceCompanyDictionary) {
      return;
    }
    const createdCompany = await globalForms.openCreateCustomDictionaryItem(
      insuranceCompanyDictionary.id,
    );
    if (createdCompany) {
      form.setValue(
        'healthInsuranceCompanyId',
        createdCompany.id,
        validateAndDirty,
      );
    }
  }, [globalForms]);

  const personalData = (
    <FieldBlock
      title={i18next.t('titles.personal_data')}
      className={styles.fieldBlock}
    >
      <Field
        className={styles.expandedFormField}
        title={i18next.t('first_name')}
        required={true}
      >
        <Input
          {...register('firstName')}
          errorText={formState.errors.firstName?.message}
          testId="patient-firstName"
        />
      </Field>
      <Field
        className={styles.expandedFormField}
        title={i18next.t('last_name')}
        required={true}
      >
        <Input
          {...register('lastName')}
          errorText={formState.errors.lastName?.message}
          testId="patient-lastName"
        />
      </Field>
      <div className={styles.row}>
        <Field className={styles.expandedFormSexField} title={i18next.t('sex')}>
          <Controller
            control={control}
            name={'sex'}
            render={({ field: { onChange, value } }) => (
              <DropDownInput
                useIdFunctionAsValue={true}
                required={false}
                variant="formInput"
                getOptionLabel="displayValue"
                idFunction="value"
                errorText={formState.errors.sex?.message}
                options={sexTypes.map((sexType) => {
                  return {
                    value: sexType,
                    displayValue: localizeSexType(sexType),
                  };
                })}
                value={value}
                onValueChanged={(option) => {
                  onChange(option?.value ?? SexType.Undefined);
                }}
                testId="patient-sex"
              />
            )}
          />
        </Field>
        <Field
          className={styles.expandedFormSexField}
          title={i18next.t('title')}
        >
          <ComboBoxInput
            required={false}
            variant={'formInput'}
            options={personTitles}
            value={
              personTitles.find((x) => x.id === customValues[titleKey]) ??
              customValues[titleKey]
            }
            onValueChanged={(customValue, item) => {
              if (item) {
                customValues[titleKey] = item.id;
              } else {
                customValues[titleKey] = customValue;
              }
              setValue(nameof('values'), customValues, validateAndDirty);
            }}
            labelFunction={(item) => {
              return item.value ? i18nextDict.t(item.value) : item;
            }}
            testId="patient-title"
          />
        </Field>
      </div>
      <Field
        className={styles.expandedFormField}
        title={i18next.t('date_of_birth')}
        required={true}
      >
        <Controller
          name={'dateOfBirth'}
          control={control}
          render={({ field: { onChange, value } }) => (
            <DatePicker
              error={!!formState.errors.dateOfBirth}
              errorText={(formState.errors.dateOfBirth as any)?.message}
              minDate={minBirthDate}
              value={value}
              onDateChanged={onChange}
              onInvalidDate={onChange}
              allowToSelectYear={true}
              testId="patient-dateOfBirth"
              disableInternalErrors={true}
            />
          )}
        />
      </Field>
      {isDeceasedEnabled && (
        <>
          <Field
            className={styles.expandedFormField}
            title={i18next.t('date_of_death')}
          >
            <Controller
              name={'dateOfDeath'}
              defaultValue={null}
              control={control}
              render={({ field: { onChange, value } }) => (
                <DatePicker
                  error={!!formState.errors.dateOfDeath}
                  errorText={(formState.errors.dateOfDeath as any)?.message}
                  value={value}
                  onDateChanged={(newDate) => {
                    onChange(newDate);
                    dateOfDeathOnChange(newDate);
                  }}
                  onInvalidDate={onChange}
                  testId="patient-dateOfDeath"
                  disableInternalErrors={true}
                />
              )}
            />
          </Field>
          <CheckBox
            className={styles.checkbox}
            {...register('isDeceased')}
            title={i18next.t('is_deceased')}
            testId="patient-isDeceased"
          />
        </>
      )}
    </FieldBlock>
  );

  const addressDetails = (
    <FieldBlock
      title={i18next.t('titles.address_details')}
      className={styles.fieldBlock}
    >
      <Field className={styles.expandedFormField} title={i18next.t('address')}>
        <Input
          {...register('address')}
          errorText={formState.errors.address?.message}
          testId="patient-address"
        />
      </Field>
      <Field className={styles.expandedFormField} title={i18next.t('location')}>
        <Input
          {...register('location')}
          errorText={formState.errors.location?.message}
          testId="patient-location"
        />
      </Field>
      <Field
        className={styles.expandedFormPostcodeField}
        title={i18next.t('postcode')}
      >
        <Input
          {...register('postcode')}
          errorText={formState.errors.postcode?.message}
          testId="patient-postcode"
        />
      </Field>
    </FieldBlock>
  );

  function getLinkProps(
    contactType: ContactType,
    field: keyof PatientData,
  ): FieldLinkProps | undefined {
    if (priorityContactType === contactType) {
      return {
        title: <PriorityBadge />,
      };
    }
    const contact = getValues(nameof(field));
    if (!contact?.trim()) {
      return undefined;
    }

    return {
      title: i18next.t('operations.make_priority'),
      className: styles.link,
      onClick: () => changePriorityContactType(contactType),
    };
  }

  const contacts = (
    <FieldBlock
      title={i18next.t('titles.contacts')}
      className={styles.fieldBlock}
      error={
        formState.errors.contact?.message ||
        formState.errors.priorityContactType?.message
      }
    >
      <Field
        className={styles.expandedFormField}
        title={i18next.t('mobile_phone')}
        linkProps={getLinkProps(ContactType.MobilePhone, 'mobilePhone')}
      >
        <Input
          {...register('mobilePhone')}
          errorText={
            formState.errors.mobilePhone?.message ||
            (priorityContactType === ContactType.MobilePhone &&
              formState.errors.priorityContact?.message) ||
            undefined
          }
          testId="patient-mobilePhone"
        />
      </Field>
      <Field
        className={styles.expandedFormField}
        title={i18next.t('home_phone')}
        linkProps={getLinkProps(ContactType.HomePhone, 'homePhone')}
      >
        <Input
          {...register('homePhone')}
          errorText={
            formState.errors.homePhone?.message ||
            (priorityContactType === ContactType.HomePhone &&
              formState.errors.priorityContact?.message) ||
            undefined
          }
          testId="patient-homePhone"
        />
      </Field>
      <Field
        className={styles.expandedFormField}
        title={i18next.t('work_phone')}
        linkProps={getLinkProps(ContactType.WorkPhone, 'workPhone')}
      >
        <Input
          {...register('workPhone')}
          errorText={
            formState.errors.workPhone?.message ||
            (priorityContactType === ContactType.WorkPhone &&
              formState.errors.priorityContact?.message) ||
            undefined
          }
          testId="patient-workPhone"
        />
      </Field>
      <Field
        className={styles.expandedFormField}
        title={i18next.t('email')}
        linkProps={getLinkProps(ContactType.Email, 'email')}
      >
        <Input
          {...register('email')}
          errorText={
            formState.errors.email?.message ||
            (priorityContactType === ContactType.Email &&
              formState.errors.priorityContact?.message) ||
            undefined
          }
          testId="patient-email"
        />
      </Field>
      <Controller
        /*Using a controller here because from checkboxes break
              when name is not unique in the whole DOM tree*/
        control={form.control}
        name="emailsAllowed"
        render={({ field: { onChange, value } }) => (
          <CheckBox
            className={styles.checkbox}
            title={i18next.t('emails_allowed')}
            onChange={onChange}
            checked={value ?? undefined}
            testId="patient-emailsAllowed"
          />
        )}
      />
    </FieldBlock>
  );

  const externalNumberAsLinkProps = useMemo(
    () => ({
      title: <ExternalPatientNumberBadge />,
    }),
    [],
  );

  const clinical = (
    <FieldBlock
      title={i18next.t('titles.clinical')}
      className={styles.fieldBlock}
    >
      <Field
        className={styles.expandedFormField}
        title={i18next.t('number')}
        linkProps={
          form.getValues().numberSource === NumberSource.External
            ? externalNumberAsLinkProps
            : undefined
        }
      >
        <Input
          {...register('number')}
          disabled={!claims.permissions.editExternalPatientNumber}
          errorText={formState.errors.number?.message}
          testId="patient-number"
        />
      </Field>
      {isSubtenantEnabled && (
        <Field
          className={styles.expandedFormField}
          title={i18next.t('subtenant')}
          required={true}
        >
          <Controller
            control={control}
            name={'subtenantId'}
            render={({ field: { onChange, onBlur, value } }) => (
              <DropDownInput
                useIdFunctionAsValue={true}
                required
                variant="formInput"
                getOptionLabel="name"
                idFunction="id"
                errorText={formState.errors.subtenantId?.message}
                options={subtenants}
                value={value}
                onValueChanged={(subtenant) => {
                  onChange(subtenant.id);
                  onBlur();
                }}
                testId="patient-subtenantId"
              />
            )}
          />
        </Field>
      )}
    </FieldBlock>
  );

  const doctor = (
    <FieldBlock title={i18next.t('doctor')} className={styles.doctorBlock}>
      <Field
        className={styles.expandedFormField}
        title={i18next.t('doctor')}
        linkProps={
          claims.permissions.doctorManagement
            ? {
                title: i18next.t('operations.add_new_doctor'),
                onClick: () => addNewDoctor(),
              }
            : undefined
        }
      >
        <HookFormDropDownInput
          control={control}
          name={'doctorId'}
          enableSearch={true}
          useVirtualization={true}
          idFunction={'id'}
          getOptionLabel={getDoctorFullName}
          useIdFunctionAsValue={true}
          testId="patient-doctorId"
          options={doctors}
        />
      </Field>
    </FieldBlock>
  );

  const insurance = (
    <FieldBlock
      title={i18next.t('titles.insurance_company')}
      className={styles.insuranceBlock}
    >
      <Field
        className={styles.expandedFormField}
        title={i18next.t('health_insurance_company')}
        linkProps={
          claims.permissions.dictionariesManagement
            ? {
                title: i18next.t('operations.add_new_insurance_company'),
                onClick: () => addNewInsuranceCompany(),
                disabled: isSelfPayer,
              }
            : undefined
        }
      >
        <HookFormDropDownInput
          control={control}
          name={'healthInsuranceCompanyId'}
          getOptionLabel="value"
          idFunction="id"
          options={insuranceCompanies}
          testId="patient-healthInsuranceCompanyId"
          loading={isLoading}
          disabled={isSelfPayer}
          enableSearch={true}
          // onSearch={setInsuranceCompaniesSearchQuery}
          //calloutMaxHeight={300}
        />
      </Field>
      <Field
        className={styles.expandedFormField}
        title={i18next.t('insurance_number')}
      >
        <Input
          {...register('insuranceNumber')}
          disabled={isSelfPayer}
          errorText={formState.errors.insuranceNumber?.message}
          testId="patient-insuranceNumber"
        />
      </Field>
      <CheckBox
        className={styles.checkbox}
        disabled={isSelfPayer}
        {...register('isPrivateInsurance')}
        title={i18next.t('private_insurance')}
      />
      <CheckBox
        className={styles.checkbox}
        {...register('isSelfPayer')}
        title={i18next.t('self_payer')}
      />
    </FieldBlock>
  );

  const commentsBlock = (
    <FieldBlock title={i18next.t('comments')} className={styles.commentsBlock}>
      <Field className={styles.expandedFormField} title={i18next.t('comments')}>
        <TagsEditor
          value={comments ?? ''}
          onChange={(e, value) =>
            setValue('comments', value, { shouldDirty: true })
          }
          isMultiline={true}
          isHeightStable={true}
          maxLength={365}
        />
      </Field>
    </FieldBlock>
  );

  const levelOfCareBlock = (
    <FieldBlock
      title={i18next.t('titles.level_of_care')}
      className={styles['level-of-care-block']}
    >
      <CheckBox
        className={styles.levelOfCareNotSelectedCheckbox}
        title={i18nGlobal.t('uikit.inputs.nothing_selected')}
        checked={!customValues[levelOfCareKey]}
        onChange={(e) => {
          customValues[levelOfCareKey] = e.target.checked
            ? null
            : levelsOfCare[0]?.id;
          setValue(nameof('values'), customValues, validateAndDirty);
        }}
      />
      <RadioButtonGroup
        required={true}
        options={levelsOfCare.map((item) => ({
          id: item.id,
          value: i18nextDict.t(item.value as any, { nsSeparator: false }),
        }))}
        value={
          levelsOfCare.find((item) => item.id === customValues[levelOfCareKey])
            ?.id
        }
        labelField={'value'}
        valueField={'id'}
        onSelectedOptionChanged={(option) => {
          customValues[levelOfCareKey] = option?.id ?? null;
          setValue(nameof('values'), customValues, validateAndDirty);
        }}
        rootClassName={styles.levelOfCareRadioGroup}
        radioButtonClassName={styles.levelOfCareRadioButton}
      />
    </FieldBlock>
  );

  const CustomValuesWrapper: React.FC<React.PropsWithChildren> = useCallback(
    (props) => (
      <div
        className={styles.expandedDataContainer}
        data-test-id="patientCustomValues"
      >
        <FieldBlock
          title={i18next.t('titles.custom_values')}
          className={styles.customValuesFieldBlock}
        >
          <div className={styles.customValuesFieldBlockContent}>
            {props.children}
          </div>
        </FieldBlock>
      </div>
    ),
    [],
  );
  return (
    <div data-test-id="patientExpandedForm">
      <div className={styles.expandedDataContainer}>
        <div className={styles.expandedDataColumn}>{personalData}</div>
        <div className={styles.expandedDataColumn}>
          {contacts}
          {doctor}
        </div>
        <div className={styles.expandedDataColumn}>
          {addressDetails}
          {commentsBlock}
        </div>
        <div className={styles.expandedDataColumn}>
          {clinical}
          {insurance}
          {levelOfCareBlock}
        </div>
      </div>
      <CustomPatientFields
        patientForm={props.form}
        wrapper={CustomValuesWrapper}
        onIsValidChange={props.onIsValidChange}
        getCustomFieldsBeforeSave={props.beforeSaveRef}
        showCustomFieldsErrors={props.showCustomFieldsErrors}
        onDirtyChange={onDirtyChange}
      />
    </div>
  );
};
